/**
 * 视频独立风格库 — 2026-06-01。与海报 styleOptions.ts 解耦。
 *
 * 按 medium(媒介)分组:视频里 2D/3D/实拍 是第一感知差异,媒介本身即分组维度,
 * 也直接根治"全是 2D"(导演据 medium 出对应媒介)。
 *
 * tone 为运动向(材质/媒介/色调/光影/运动),**无静态版式/文字/留白词**(那是给海报的);
 * 带英文 art anchor(出图/视频模型对英文标签训练密度高)。
 */

export type VideoMediumKey =
  | "live-action"
  | "cinematic-3d"
  | "motion-graphics"
  | "2d-anim"
  | "painterly"
  | "stop-motion";

export interface VideoStyleOption {
  /** 注入 LLM 的运动向 tone;null = 自由发挥不注入。 */
  tone: string | null;
  /** UI 短名 */
  label: string;
  /** 稳定 key:样例图 + 选中标识 + 持久化 styleCode */
  key?: string;
  /** 静态兜底样例文件名(复用海报库,仅当与 key 不同名时填);缺省用 key。 */
  sampleKey?: string;
  /** 媒介硬约束 */
  medium?: VideoMediumKey;
  /** 一句话描述,画廊显示 */
  blurb?: string;
}

export interface VideoMediumMeta {
  key: VideoMediumKey;
  title: string;
  hint: string;
}

/** 媒介分组(画廊 sticky 标题),顺序即展示顺序 */
export const VIDEO_MEDIA: readonly VideoMediumMeta[] = [
  { key: "live-action", title: "实拍电影", hint: "真实可信,B2B 与品牌大片" },
  { key: "cinematic-3d", title: "三维视觉", hint: "产品展示 / 科技 / IP" },
  { key: "motion-graphics", title: "动态图形", hint: "数据 / 图标 / 文字动画,解说转化" },
  { key: "2d-anim", title: "二维动画", hint: "手绘 / 漫画 / 解说" },
  { key: "painterly", title: "绘画质感", hint: "水墨 / 水彩 / 油画动效" },
  { key: "stop-motion", title: "定格特色", hint: "定格等独特记忆点" },
];

/** 自由发挥(默认):tone=null,导演据内容+目的自判媒介。常驻首位。 */
export const FREE_VIDEO_STYLE: VideoStyleOption = { tone: null, label: "自由发挥" };

/** 31 个视频风格。★ = 视频新增/视频专属,其余继承海报但 tone 重写为运动向。 */
export const PRESET_VIDEO_STYLES: readonly VideoStyleOption[] = [
  // ── 实拍/电影 ──
  {
    key: "photography",
    label: "写实摄影",
    medium: "live-action",
    blurb: "实拍门店/团队/产品,自然光跟拍",
    tone: "真实商业摄影质感,实拍门店/团队/产品,自然光或暖调棚拍,浅景深虚化突出主体,手持跟拍的真实运动。commercial photography, natural light, shallow depth of field, handheld follow",
  },
  {
    key: "corporate",
    label: "实拍商务",
    medium: "live-action",
    blurb: "办公/会议/握手,稳定运镜 B2B",
    tone: "企业商务实拍,办公会议握手场景,克制稳定的运镜与推轨,深蓝商务调性,专业可信。corporate live-action, office, steady dolly, trustworthy",
  },
  {
    key: "cinematic-film",
    label: "电影质感",
    medium: "live-action",
    blurb: "电影级调色+大景深+运动长镜",
    tone: "电影级质感,戏剧性打光与大景深,流畅的运动长镜与缓推,胶片调色,品牌大片的高级氛围。cinematic film, dramatic lighting, anamorphic, smooth tracking long take, color grade",
  },
  {
    key: "minimal",
    label: "高级极简",
    medium: "live-action",
    blurb: "单产品极简,缓推环绕,看起来贵",
    tone: "高端极简,黑白与金灰,单一产品精致摆放,缓推与环绕展示金属反光,克制贵气。luxury minimalist, slow push, product turntable, refined",
  },
  {
    key: "hk-film",
    sampleKey: "hk-90s",
    label: "港风胶片",
    medium: "live-action",
    blurb: "胶片颗粒+霓虹,复古港片运镜",
    tone: "九十年代港片质感,胶片颗粒叠霓虹招牌红黄蓝,旧街市运动镜头,暖调做旧,市井电影感。Wong Kar-wai, 90s HK cinema, film grain, neon, moody tracking",
  },
  {
    key: "wuxia",
    label: "国风武侠",
    medium: "live-action",
    blurb: "衣袂翻飞+刀光,水墨飞溅,电影动作",
    tone: "国风武侠电影感,侠客白衣衣袂翻飞,刀光剑影与水墨飞溅,竹林雪山薄雾,青灰冷调点朱红,电影级动作运镜。wuxia film, flowing robes, ink splash action, cinematic",
  },
  // ── 三维CG ──
  {
    key: "ai-future",
    label: "科技AI未来",
    medium: "cinematic-3d",
    blurb: "数据光带流动+AI中枢旋转,粒子穿梭",
    tone: "AI 科技未来质感,深空蓝电光紫,中央 AI 中枢球体旋转发光,数据光带流动、粒子穿梭,玻璃拟态。futuristic AI 3D, glowing neural core rotating, particle flow, blue-purple",
  },
  {
    key: "product-cg",
    label: "产品三维特写",
    medium: "cinematic-3d",
    blurb: "三维产品环绕/爆炸图/材质特写 ★",
    tone: "三维产品 CG 展示,产品环绕旋转、爆炸拆解图、材质高光特写,影棚布光与反射,干净背景突出产品。3D product render, turntable, exploded view, material close-up, studio HDRI, octane",
  },
  {
    key: "scifi",
    label: "科幻大片",
    medium: "cinematic-3d",
    blurb: "宇宙星门+光束推进,史诗体积光",
    tone: "科幻电影史诗感,浩瀚星河发光星门,光束推进与星舰穿梭,冷蓝紫叠暖金光晕,电影级体积光大景深。epic sci-fi 3D, cosmic gate, beam push-in, volumetric light",
  },
  {
    key: "3d-toy",
    label: "3D潮玩",
    medium: "cinematic-3d",
    blurb: "软萌IP蹦跳挥手,糖果撞色",
    tone: "3D 渲染潮玩 IP,圆润软萌角色蹦跳挥手,糖果撞色叠发光图标,影棚柔光高光,年轻记忆点。3D blender designer toy, bouncy cute mascot, candy colors, glossy",
  },
  {
    key: "pixar-3d",
    label: "皮克斯3D",
    medium: "cinematic-3d",
    blurb: "皮克斯角色表情动作,电影布光",
    tone: "皮克斯式 3D 动画,圆润大眼角色丰富表情与动作,蜡质次表面散射皮肤,电影级布光暖调浅景深。Pixar 3D animation, expressive character motion, subsurface skin, soft cinematic",
  },
  {
    key: "cyberpunk",
    label: "赛博朋克",
    medium: "cinematic-3d",
    blurb: "霓虹雨夜穿行+故障扫描,高对比",
    tone: "赛博朋克未来都市,霓虹紫粉对撞电光青,雨夜街道穿行运镜,全息招牌与故障扫描线,高对比反乌托邦。cyberpunk 3D, neon rainy city fly-through, glitch, holographic",
  },
  {
    key: "surreal",
    label: "超现实概念",
    medium: "cinematic-3d",
    blurb: "漂浮错位物体缓动,梦境概念",
    tone: "超现实梦境,违反逻辑的漂浮物体缓慢运动与错位比例,柔和渐变天空诡谲阴影,达利式融化,概念主视觉。surrealism 3D, floating drifting objects, Dali, impossible scale",
  },
  {
    key: "cel-3d",
    label: "卡通渲染",
    medium: "cinematic-3d",
    blurb: "cel-shaded 三维+硬描边,游戏动画感 ★",
    tone: "卡通渲染 cel-shaded 三维,硬边色块平涂叠黑描边,角色流畅运动与运镜,鲜明撞色,游戏/动画质感。cel-shaded 3D, NPR toon shading, bold outlines, anime cel look, dynamic motion, Guilty Gear style",
  },
  {
    key: "mecha",
    label: "机甲高达",
    medium: "cinematic-3d",
    blurb: "机甲变形+金属反光,科技发布高燃 ★",
    tone: "机甲高达三维,精密机甲变形展开、推进器喷射、金属反光与火花,镜头环绕仰拍,科技发布的高燃震撼。mecha gundam 3D, transforming robot, thruster burst, metallic reflection, epic low-angle orbit, sparks",
  },
  // ── MG动效 ──
  {
    key: "mg-flat",
    sampleKey: "flat-illustration",
    label: "MG扁平动效",
    medium: "motion-graphics",
    blurb: "扁平图标/人物/箭头逐个亮起流动 ★",
    tone: "扁平 MG 动画,等距图标人物与箭头流程线逐个亮起、滑入、流动,清爽撞色圆角几何,讲清流程功能。flat motion graphics, isometric, icons sliding in, process flow animation",
  },
  {
    key: "data-viz",
    sampleKey: "dashboard",
    label: "数据可视化",
    medium: "motion-graphics",
    blurb: "图表生长+数字滚动+环形仪表 ★",
    tone: "数据可视化动效,折线图表生长、数字滚动跳动、环形仪表填充,深色背景霓虹蓝绿数据光,秀数据秀能力。data viz motion, growing charts, rolling numbers, gauge fill, dark HUD",
  },
  {
    key: "kinetic-type",
    label: "动态文字",
    medium: "motion-graphics",
    blurb: "文字排版动效,金句逐字弹出 ★",
    tone: "动态文字排版,卖点金句逐字弹出、缩放、强调,撞色背景与节奏卡点,信息冲击强。kinetic typography, words popping scaling, bold color, rhythmic emphasis",
  },
  {
    key: "hard-sell",
    label: "强转化广告",
    medium: "motion-graphics",
    blurb: "撞色大字砸屏+利益点放大,快切",
    tone: "强转化信息流广告,高饱和撞色大字砸屏、利益点放大弹出、快切节奏,紧迫促转化。high-impact direct-response motion, slamming bold text, fast cuts, urgency",
  },
  {
    key: "y2k",
    label: "Y2K千禧",
    medium: "motion-graphics",
    blurb: "铬金属液态字+果冻气泡弹动",
    tone: "Y2K 千禧动效,银色铬金属液态字流动、果冻半透明气泡弹动、星形闪光,蓝粉紫塑料光泽。Y2K chrome motion, liquid metal, jelly bubbles bouncing, sparkles",
  },
  // ── 二维动画 ──
  {
    key: "anime",
    label: "日系动漫",
    medium: "2d-anim",
    blurb: "通透天空+柔光,治愈手绘动起来",
    tone: "日系动漫,新海诚通透天空柔光云层流动,低饱和水彩质感圆润手绘线条,光斑飘动,温柔治愈。anime animation, Makoto Shinkai, drifting clouds, soft watercolor, gentle motion",
  },
  {
    key: "ghibli",
    label: "吉卜力",
    medium: "2d-anim",
    blurb: "吉卜力治愈手绘,风吹草动云卷,情感片 ★",
    tone: "吉卜力手绘动画,温暖治愈的田园场景,风吹麦浪、云朵舒卷、角色自然走动,柔和水彩质感与暖光,宫崎骏式氛围。Studio Ghibli animation, Miyazaki, hand-painted, wind-blown grass, drifting clouds, warm gentle motion",
  },
  {
    key: "shonen",
    label: "热血少年漫",
    medium: "2d-anim",
    blurb: "高速动作线+爆发特效,热血燃 ★",
    tone: "热血少年漫动画,高速动作与速度线、冲击波特效、夸张运镜与爆发瞬间,鲜明撞色描边,燃系节奏。shonen anime, dynamic action lines, speed effects, impact frames, explosive motion, bold outlines, high energy",
  },
  {
    key: "manhua",
    sampleKey: "manhua-drama",
    label: "国漫厚涂",
    medium: "2d-anim",
    blurb: "厚涂人物+戏剧光影,情绪特写镜头感",
    tone: "国漫厚涂风,精致人物与戏剧化光影,情绪特写与镜头推拉,饱和厚涂上色电影氛围。Chinese manhua animation, thick-paint, dramatic lighting, emotional close-up, camera push",
  },
  {
    key: "whiteboard",
    sampleKey: "doodle",
    label: "手绘白板",
    medium: "2d-anim",
    blurb: "手绘线条逐笔画出+图标拼接,解说 ★",
    tone: "手绘白板解说动画,线条逐笔画出、图标逐个拼接出现,马克笔质感简洁线稿,清晰讲解流程。whiteboard explainer animation, hand-drawn lines drawing on, marker, icons assembling",
  },
  {
    key: "motion-comic",
    sampleKey: "comic",
    label: "动态漫画",
    medium: "2d-anim",
    blurb: "漫画分格+网点+爆炸字,镜头推拉 ★",
    tone: "动态漫画 motion comic,漫画分格切换、Ben-Day 网点、爆炸拟声大字,镜头在格间推拉摇移,波普张力。motion comic, panel transitions, Ben-Day dots, camera pan across panels",
  },
  // ── 绘画质感 ──
  {
    key: "ink-wash",
    label: "水墨动画",
    medium: "painterly",
    blurb: "墨色晕染流动+飞鸟掠过,中国风",
    tone: "中国风水墨动画,墨色浓淡晕染流动扩散,远山飞鸟掠过,朱红印章,禅意素雅。Chinese ink wash animation, flowing ink bleed, birds gliding, sumi-e, zen",
  },
  {
    key: "guochao",
    label: "国潮新中式",
    medium: "painterly",
    blurb: "青绿山水+描金缠枝生长,国潮",
    tone: "国潮新中式动效,青绿山水祥云流动,描金缠枝花纹生长,朱砂印章,东方意境运动。new Chinese guochao motion, ink mountains, growing gold filigree, vermillion",
  },
  {
    key: "watercolor",
    label: "清透水彩",
    medium: "painterly",
    blurb: "颜料晕染扩散+边缘渗色,清新通透",
    tone: "清透水彩动效,湿画法颜料流动扩散、边缘渗色晕开,浅淡通透色层,清新雅致。watercolor motion, wet-on-wet color bleeding spreading, translucent, fresh",
  },
  {
    key: "oil-painting",
    label: "古典油画",
    medium: "painterly",
    blurb: "厚涂笔触流动+伦勃朗光,油画质感",
    tone: "古典油画动效,厚涂笔触流动、丰富层次,暖金褐深棕伦勃朗光影,戏剧明暗,画布肌理高级感。oil painting motion, flowing impasto brushstrokes, Rembrandt light, canvas",
  },
  // ── 特色 ──
  {
    key: "stop-motion",
    sampleKey: "claymation",
    label: "定格动画",
    medium: "stop-motion",
    blurb: "黏土/纸偶逐帧跳动,手作温度 ★",
    tone: "定格动画质感,黏土或纸偶角色逐帧跳动的微颤运动,手作肌理与暖光,独特记忆点。stop-motion, claymation, paper puppets, frame-by-frame jitter, handcrafted, warm",
  },
];

export const VIDEO_STYLES: readonly VideoStyleOption[] = [FREE_VIDEO_STYLE, ...PRESET_VIDEO_STYLES];

/** 按 tone 找媒介(前端选中时带上 medium,作 ai-service 兜底)。 */
export function mediumForTone(tone: string | null): VideoMediumKey | undefined {
  if (!tone) return undefined;
  return PRESET_VIDEO_STYLES.find((s) => s.tone === tone)?.medium;
}

/** 当前 tone 是否用户自定义(非 null 且不命中任何预设)。 */
export function isCustomVideoTone(selected: string | null): boolean {
  if (selected === null) return false;
  return !PRESET_VIDEO_STYLES.some((s) => s.tone === selected);
}

/** 按 tone 找预设(回显选中态 / 取 label / key)。 */
export function videoStyleByTone(tone: string | null): VideoStyleOption | undefined {
  if (!tone) return undefined;
  return PRESET_VIDEO_STYLES.find((s) => s.tone === tone);
}

/** 按 key 找预设。 */
export function videoStyleByKey(key?: string | null): VideoStyleOption | undefined {
  if (!key) return undefined;
  return PRESET_VIDEO_STYLES.find((s) => s.key === key);
}

/**
 * tone → 短标签(详情/概览回显用)。命中预设返 label;自定义截断 tone 文本;null 返 null。
 * 注意:**别用海报的 styleLabelForTone**,视频 tone 与海报库不同(会全部回退"自定义")。
 */
export function videoStyleLabelForTone(tone: string | null): string | null {
  if (!tone) return null;
  const hit = videoStyleByTone(tone);
  if (hit) return hit.label;
  // 自定义自由描述:取首句/前 12 字,避免把 100+ 字 tone 整段塞进徽标。
  const t = tone.trim();
  return t.length > 14 ? `${t.slice(0, 14)}…` : t;
}

/** styleCode(=预设 key)或 tone → 标签。详情页优先用持久化的 styleCode。 */
export function videoStyleLabel(styleCode?: string | null, tone?: string | null): string | null {
  const byCode = videoStyleByKey(styleCode);
  if (byCode) return byCode.label;
  return videoStyleLabelForTone(tone ?? null);
}

/**
 * 样例图 URL —— 静态 webp(视频专属优先,缺则复用海报库 sampleKey 映射),
 * 缺图时由 thumb 内 onError 回退渐变占位。(试过动图预览观感差,改回纯静态。)
 */
export const POSTER_STYLE_SAMPLE_DIR = "/style-samples";

/**
 * ★还没有样例图的视频专属风格(public/style-samples 下没有同名 webp,海报库也没有可借的)。
 *
 * 1005 查清:「产品三维特写」(product-cg)、「电影质感」(cinematic-film)、「动态文字」(kinetic-type)
 * 是视频独有风格,文件从来没生成过 —— 此前照样拼 URL 去打一个 404,靠 onError 退成一块纯渐变,
 * 看上去就是「这张卡没图」。登记在这里 → 不发请求,直接走设计化兜底(VideoStyleThumb)。
 * 补了图就把它从这里删掉;守卫测试会双向校验(登记的确实没文件 / 没登记的确实有文件)。
 */
export const VIDEO_STYLE_SAMPLE_PENDING: ReadonlySet<string> = new Set([
  "cinematic-film",
  "product-cg",
  "kinetic-type",
]);

/** 样例文件名(不含扩展名):有 sampleKey(映射到海报文件)用之,否则 key 同名。 */
export function videoStyleSampleFile(opt?: VideoStyleOption): string | undefined {
  if (!opt?.key) return undefined;
  return opt.sampleKey ?? opt.key;
}

/** 静态样例:有 sampleKey(映射到海报文件)用之,否则用 key 同名;登记为「待补图」的不给 URL。 */
export function videoStyleStaticUrl(opt?: VideoStyleOption): string | undefined {
  const file = videoStyleSampleFile(opt);
  if (!file || VIDEO_STYLE_SAMPLE_PENDING.has(file)) return undefined;
  return `${POSTER_STYLE_SAMPLE_DIR}/${file}.webp`;
}

/** 确定性渐变占位(样例未就绪时背景),按 key 散列选色对。 */
export function videoStylePlaceholderGradient(key?: string): string {
  const pairs = [
    ["#6366f1", "#a855f7"],
    ["#0ea5e9", "#22d3ee"],
    ["#f59e0b", "#ef4444"],
    ["#10b981", "#14b8a6"],
    ["#ec4899", "#8b5cf6"],
    ["#64748b", "#94a3b8"],
    ["#f43f5e", "#fb923c"],
    ["#3b82f6", "#6366f1"],
  ];
  let h = 0;
  const s = key || "x";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const [a, b] = pairs[h % pairs.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

/**
 * 1004 第三轮:风格条按行业过滤 —— 与 ai 军师侧 `video_styles.recommended_gallery(industry_code)` 同一口径。
 * ★两份名单与 ai `poster_styles.CONSUMER_INDUSTRIES` / `video_styles.CONSUMER_GALLERY_KEYS` **逐字一致**,改一边要改另一边。
 * 消费向行业(珠宝/美妆/食品/零售…)卖的是实物,动漫 / 科幻 / 潮玩 / 扁平动效这类画风会把产品画成别的东西。
 */
export const CONSUMER_INDUSTRIES: readonly string[] = [
  "IND_JEWELRY",
  "IND_BEAUTY",
  "IND_FOOD_BEV",
  "IND_F_AND_B",
  "IND_RETAIL",
  "IND_ECOMMERCE",
  "IND_HOME_SERVICE",
  "IND_LOCAL_SERVICE",
  // 1027 行业波:零售 / 本地生活两大类下卖实物、做到店的新细分码(这里是精确匹配,不走父码链,要逐个列)
  "IND_HEALTH_FOOD",
  "IND_SMART_HOME",
  "IND_FURNITURE",
  "IND_APPAREL",
  "IND_DAILY_CHEM",
  "IND_HOME_APPLIANCE",
  "IND_SPORTS_OUTDOOR",
  "IND_PET",
  "IND_TOURISM",
  "IND_WELLNESS_SERVICE",
  "IND_PET_FRANCHISE",
];

/** 消费向行业的推荐风格:实拍 / 极简 / 强转化 + 港风 / 国潮 / 水彩(节日、礼赠、门店常用)。 */
export const CONSUMER_VIDEO_STYLE_KEYS: readonly string[] = [
  "photography",
  "minimal",
  "hard-sell",
  "hk-film",
  "guochao",
  "watercolor",
];

export function isConsumerIndustry(industryCode?: string | null): boolean {
  return !!industryCode && CONSUMER_INDUSTRIES.includes(industryCode.trim().toUpperCase());
}

/** 该行业的推荐风格 key 集;非消费向 / 取不到行业 → null(= 不过滤,旧口径)。 */
export function recommendedVideoStyleKeys(industryCode?: string | null): readonly string[] | null {
  return isConsumerIndustry(industryCode) ? CONSUMER_VIDEO_STYLE_KEYS : null;
}

/**
 * 风格条上铺哪几张:消费向行业 → 推荐那几张;其余 → 每个媒介的第一张(旧口径逐字不变)。
 */
export function stripVideoStyles(industryCode?: string | null): VideoStyleOption[] {
  const keys = recommendedVideoStyleKeys(industryCode);
  if (keys) {
    return keys
      .map((k) => PRESET_VIDEO_STYLES.find((s) => s.key === k))
      .filter((s): s is VideoStyleOption => !!s);
  }
  return VIDEO_MEDIA.map((m) => PRESET_VIDEO_STYLES.find((s) => s.medium === m.key)).filter(
    (s): s is VideoStyleOption => !!s
  );
}
